import React, { useState, useCallback, useContext } from 'react';
import { useQueryClient } from 'react-query';
import {
  Track,
  trackActions,
  useCurrentUser,
  useDispatch,
  useIsProMember,
} from '../../../api';

import DeleteTrackModal from '../Modal/DeleteVirpp';
import ActivateTrackModal from '../Modal/ActivateVirppModal';
import ActivatedTrackModal from '../Modal/ActivatedVirpp';

export interface TrackContextValue {
  track: Track;
  isTrackOwner: boolean;
  onDelete?: () => void;
  onActivate?: () => void;
}

const TrackContext = React.createContext<TrackContextValue>({
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  track: undefined,
  onDelete: () => null,
  onActivate: () => null,
});

const { Provider, Consumer } = TrackContext;

export const useTrack = () => {
  const { track, onActivate, onDelete } = useContext(TrackContext);

  return [track, { onActivate, onDelete }] as const;
};

const TrackProvider: React.FC<{ track: Track }> = ({ track, children }) => {
  const dispatch = useDispatch();

  const currentUser = useCurrentUser();
  const isCurrentUserPro = useIsProMember();
  const isTrackOwner = currentUser.id === track?.user.id;

  const queryCache = useQueryClient();

  const [action, setAction] = useState<'activate' | 'activated' | 'delete'>();

  const onDismiss = useCallback(() => setAction(undefined), [setAction]);

  const onAfterDelete = useCallback(async () => {
    onDismiss();

    // For backwards computability on profile
    await dispatch(trackActions.deleteTrack(track.id));

    queryCache.refetchQueries(['my-tracks']);
  }, [dispatch, onDismiss, queryCache, track.id]);

  const onAfterActivate = useCallback(async () => {
    onDismiss();

    // For backwards computability on profile
    await dispatch(trackActions.activateTrack(track.id));

    await queryCache.refetchQueries(['my-tracks']);

    setAction('activated');
  }, [dispatch, onDismiss, queryCache, track.id]);

  const onDelete = useCallback(() => setAction('delete'), [setAction]);

  const onActivate = useCallback(() => {
    /**
     * In cases BE for some reason returns archived tracks and user is a pro member
     * we need to skip the `activate` modal and proceed to activating the track instead
     * to avoid confusion.
     */
    if (isCurrentUserPro) {
      return onAfterActivate();
    }

    return setAction('activate');
  }, [isCurrentUserPro, onAfterActivate]);

  return (
    <Provider
      value={{
        track,
        isTrackOwner,
        onDelete: isTrackOwner ? onDelete : undefined,
        onActivate:
          isTrackOwner && track.status === 'archived' ? onActivate : undefined,
      }}
    >
      {children}
      {isTrackOwner && (
        <>
          {track.status === 'archived' && (
            <>
              <ActivateTrackModal
                track={track}
                visible={action === 'activate'}
                onDismiss={onDismiss}
                onConfirm={onAfterActivate}
              />
            </>
          )}
          {/**
           * Don't put it in the enclosure above. Trust me
           */}
          <ActivatedTrackModal
            track={track}
            visible={action === 'activated'}
            onDismiss={onDismiss}
          />
          <DeleteTrackModal
            track={track}
            visible={action === 'delete'}
            onDismiss={onDismiss}
            onConfirm={onAfterDelete}
          />
        </>
      )}
    </Provider>
  );
};

export { TrackProvider, Consumer as TrackConsumer };
export default TrackContext;
